/*
* Various data formatter utilities & helpers
*/

import * as PNF from 'google-libphonenumber';
import * as _ from 'lodash';
import moment from 'moment';
import pluralize from 'pluralize';
import { getSyntheticTrailingComments } from '../../node_modules/typescript';

const GoogolePhoneUtil = PNF.PhoneNumberUtil.getInstance();

export class Formatters {
    public static readonly ModuleName: string = 'Formatters';
    public static readonly DefaultPhoneFormatCountry: string = 'US';
    public static readonly DefaultSeparator: string = '-';

    public static getDaysUntilDateValue(untilDate?: string) {
        let result;
        if (!untilDate) { return result; }

        const nowDate = new Date();
        const thenDate = new Date(untilDate);
        const msPerDay = 8.64e7;

        // Set to noon - avoid DST errors
        thenDate.setHours(12, 0, 0);
        nowDate.setHours(12, 0, 0);

        // Round to remove daylight saving errors
        result = Math.round( (thenDate.getTime() - nowDate.getTime()) / msPerDay );

        return result;
    }

    public static composeLocationCityState(location?: any) {
        // Return a composed location string (City, STATECODE) for valid location objects
        let result = '';

        if (!location) { return result; }

        if (location.city) { result += location.city; }
        if (location.stateCode) {
            if (result) { result += ', '; }
            result += location.stateCode;
        }

        return result;
    }

    public static composeLocationCityStateZipCountry(location?: any, appendCountry: boolean = true) {
        // Return a composed location string (City, STATECODE) for valid location objects
        let result = '';

        if (!location) { return result; }

        if (_.get(location, 'city')) { result += _.get(location, 'city'); }

        if (_.get(location, 'stateCode')) {
            if (result) { result += ', '; }
            result += _.get(location, 'stateCode');
        }

        if (_.get(location, 'postalCode')) {
            if (result) { result += ' '; }
            result += _.get(location, 'postalCode');
        }

        if (appendCountry && _.get(location, 'countryCode')) {
            if (result) { result += ' '; }
            result += _.get(location, 'countryCode');
        }

        return result;
    }

    public static composeLocationStreetAddress(location?: any) {
        // Return a composed location string (Address 1, Address 2) for valid location objects
        let result = '';

        if (!location) { return result; }

        if (location.address1) { result = `${location.address1}`; }
        if (location.address2) { result += `, ${location.address2}`; }

        return result;
    }

    public static composeLocationFullAddress(location?: any) {
        // Return a composed location string (Address 1, Address 2, City, STATECODE postalCode) for valid location objects
        let result = '';
        let missing = 'Location Data Missing';
        if (!location) { return result += `${missing}`; }

        if (location.address1) { result = `${location.address1}`; }
        if (location.address2) { result += `, ${location.address2}`; }
        if (location.city) { result += `, ${location.city}`; }
        if (location.stateCode) {
            if (result) { result += ', '; }
            result += location.stateCode;
        }
        if (location.postalCode) { result += ` ${location.postalCode}`; }
        if (location.countryCode) { result += ` ${location.countryCode}`; }

        return result;
    }

    public static composeClientContactFormal(contact?: any) {
        // Returns composed client contact name with title & salutation
        let result = '';

        if (!contact) { return result; }

        if (Formatters.getStr(contact, 'lastName')) { result += `${Formatters.getStr(contact, 'lastName')}, `; }
        if (Formatters.getStr(contact, 'salutation')) { result += `${Formatters.getStr(contact, 'salutation')} `; }
        if (Formatters.getStr(contact, 'firstName')) { result += `${Formatters.getStr(contact, 'firstName')}`; }
        if (Formatters.getStr(contact, 'title')) { result += ` - ${Formatters.getStr(contact, 'title')}`; }

        return result;
    }

    public static composeClientContactLetter(contact?: any, includeFirstName: boolean = true, includeSalutation: boolean = true) {
        // Returns composed client contact name with title & salutation
        let result = '';

        if (!contact) { return result; }

        if (includeSalutation && Formatters.getStr(contact, 'salutation')) { result += `${Formatters.getStr(contact, 'salutation')} `; }

        if (includeFirstName) {
            if (Formatters.getStr(contact, 'firstName')) { result += `${Formatters.getStr(contact, 'firstName')} `; }
        }

        if (Formatters.getStr(contact, 'lastName')) { result += `${Formatters.getStr(contact, 'lastName')}`; }

        return result;
    }

    public static composeUsernameLabel(user: any, appendUsername: boolean = true): string {
        // Builds a user name label for use in Select options
        let result = '';
        if (!user) { return result; }

        let firstName;
        let lastName;

        if (user.contact) {
            firstName = _.get(user, 'contact.firstName');
            lastName = _.get(user, 'contact.lastName');
        } else {
            firstName = _.get(user, 'firstName');
            lastName = _.get(user, 'lastName');
        }

        if (firstName) { result = firstName; }

        if (lastName) {
            if (result) { result += ' '; }
            result += lastName;
        }

        if (appendUsername && user.username) {
            if (result) {
                result += ` (${user.username})`;
            } else {
                result = user.username;
            }
        }

        return result;
    }

    public static composeEntityNameLabel(entityObj?: any, entityNameField: string = 'name', entityIdField: string = '', prependEntityId: boolean = true, prependSeparator: string = Formatters.DefaultSeparator): string {
        // Builds an entity name label (defaults to "12880 - Test")
        let result = '';

        const entityId = Formatters.getStr(entityObj, entityIdField, '');
        const entityName = Formatters.getStr(entityObj, entityNameField, '');

        if (!entityObj || (!entityName && !entityId)) { return result; }        
        
        if (prependEntityId && entityId) {
            result += `${entityId} `;       
            if (prependSeparator) {
                result += `${prependSeparator} `;
            }
        }
        
        result += entityName;
        
        return result;
    }

    public static composeAssociationNameLabel(assocObj?: any, prependAssocId: boolean = true, prependSeparator: string = Formatters.DefaultSeparator): string {
        // Builds an association name label (defaults to "12880 - Test")        
        return Formatters.composeEntityNameLabel(assocObj, 'name', 'associationId', prependAssocId, prependSeparator);
    }

    public static composeClientNameLabel(clientObj?: any, prependClientId: boolean = true, prependSeparator: string = Formatters.DefaultSeparator): string {
        // Builds an client name label (defaults to "12880 - Test")        
        return Formatters.composeEntityNameLabel(clientObj, 'name', 'clientId', prependClientId, prependSeparator);
    }

    public static composeInventorySubgroupLabel(subgroupObj?: any, prependGroupCode: boolean = true, prependSeparator: string = Formatters.DefaultSeparator): string {
        // Builds an inventory subgroup name label (defaults to "groupcode - title")        
        return Formatters.composeEntityNameLabel(subgroupObj, 'title', 'groupCode', prependGroupCode, prependSeparator);
    }

    public static capitalizeFirstLetter(s: string): string {
        // Returns s argument with very first character capitalized
        if (!s) { return ''; }

        return s[0].toUpperCase() + s.slice(1);
    }

    public static wrapParentheses(s: string): string {
        // Returns s argument wrapped in parentheses
        if (!s) { return ''; }

        return `(${s})`;
    }

    public static plural = (str: string = '', count = 0, prependCount = true) => {
        let result = '';

        if (!str) { return result; }

        return pluralize(str, count, prependCount);
    }

    /* Special string formatters */
    public static formatPercentage(parseNumber: any, decimals: number = 2) {
        // Returns a number formatted as a percentage (123.45%), respecting 'decimals' argument

        return `${parseFloat(parseNumber.toString()).toFixed(decimals)}%`;
    }

    public static formatCurrencyIntl(parseNumber: any, decimals: number = 2) {
        // Returns a number formatted as a currency ($123.45), respecting 'decimals' argument

        const num = parseFloat(parseNumber.toString());
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(num);
    }

    public static formatPercentageIntl(parseNumber: any, decimals: number = 2) {
        // Returns a number formatted as a percent $123.45%), respecting 'decimals' argument

        const num = parseFloat(parseNumber.toString());

        return new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(num);
    }

    public static formatDecimalIntl(parseNumber: any, decimals: number = 2) {
        // Returns a number formatted 123.45, respecting 'decimals' argument

        const num = parseFloat(parseNumber.toString());

        return new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(num);
    }

    public static formatIntIntl(parseNumber: any, decimals: number = 0) {
        // Returns a number formatted 123.45, respecting 'decimals' argument

        const num = parseFloat(parseNumber.toString());

        return new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(num);
    }

    public static formatPhoneNumber(phoneNumber: string, countryCode: string = Formatters.DefaultPhoneFormatCountry, pnfFormat: any = PNF.PhoneNumberFormat.INTERNATIONAL): string {
        // Returns a formatted phone number for countryCode
        if (!phoneNumber) { return phoneNumber; }

        try {
            const phoneNumberParsed = GoogolePhoneUtil.parse(phoneNumber, countryCode);

            return GoogolePhoneUtil.format(phoneNumberParsed, pnfFormat);
        } catch (e) {
            return phoneNumber;
        }
    }

    public static formatPhoneNumberNational(phoneNumber: string, countryCode: string = Formatters.DefaultPhoneFormatCountry): string {
        // Returns a formatted phone number for countryCode
        if (!phoneNumber) { return phoneNumber; }

        try {
            const phoneNumberParsed = GoogolePhoneUtil.parse(phoneNumber, countryCode);

            return GoogolePhoneUtil.format(phoneNumberParsed, PNF.PhoneNumberFormat.NATIONAL);
        } catch (e) {
            return phoneNumber;
        }
    }

    public static formatDateForKendoDateInput(date: any) {
        if (!date) { return null; }

        return moment(date).toDate();
    }

    public static formatDateForInput(date: any) {
        return moment(date).format('YYYY-MM-DD');
    }

    public static formatDateShortDate(date: any, defaultStr: string = ''): string {
        if (!date) { return defaultStr; }
        
        return moment(date).format('l');
    }

    public static formatDateLongDate(date: any, defaultStr: string = ''): string {
        if (!date) { return defaultStr; }

        return moment(date).format('LLL');
    }

    public static formatDateYearDate(date: any, defaultStr: string = ''): string {
        if (!date) { return defaultStr; }

        return moment().year(date).format('YYYY');
    }

    /* Type casting and parsing */
    public static bool(parseBool: any): boolean {
        if (!parseBool) { return false; }

        return JSON.parse(parseBool);
    }

    public static int(parseNumber: any): number {
        // Return parseInt of `parseNumber` as number
        if (!parseNumber) { return 0; }

        return parseInt(parseNumber, 10);
    }

    public static float(parseNumber: any, decimals: number = 2, multiplier: number = 1): number {
        // Return parseInt of `parseNumber` as number
        if (!parseNumber) { return 0.00; }

        const parsed = parseFloat(parseNumber) * multiplier;

        return parseFloat(parsed.toFixed(decimals));
    }

    public static getStr(obj: any, path: string, defaultStr: string = '', trimResult: boolean = false): string {
        let result: string = (defaultStr) ? defaultStr : '';

        if (!obj || !path) { return result; }

        const val = _.get(obj, path, result);

        const ret = (val) ? String(val) : String(result);

        return (trimResult) ? ret.trim() : ret;
    }

    public static getInt(obj: any, path: string, defaultValueIfNull: number = 0): number {
        let val = (_.isUndefined(_.get(obj, path)) || _.isNull(_.get(obj, path))) ? defaultValueIfNull : Formatters.int(Formatters.getStr(obj, path));

        return Number(val);
    }

    public static getFloat(obj: any, path: string, decimals: number = 2, multiplier: number = 1, defaultValueIfNull: number = 0): number {
        let val = (_.isUndefined(_.get(obj, path)) || _.isNull(_.get(obj, path))) ? defaultValueIfNull : Formatters.getStr(obj, path);

        return Number(Formatters.float(val, decimals, multiplier));
    }

    public static getBool(obj: any, path: string, defaultValueIfNull: boolean = false): boolean {
        let val = (_.get(obj, path, defaultValueIfNull)) ? true : false;

        return (val) ? true : false;
    }

    public static getBoolFalse(obj: any, path: string, defaultValueIfNull: boolean = false): boolean {
        return this.getBool(obj, path, defaultValueIfNull);
    }

    public static getBoolTrue(obj: any, path: string, defaultValueIfNull: boolean = true): boolean {
        return this.getBool(obj, path, defaultValueIfNull);
    }

    public static getOrderDashLetter(order: any): string {
        if (!_.has(order, 'dash')) { return ''; }

        return String.fromCharCode(65 + Formatters.getInt(order, 'dash'));
    }

    public static getCustomRoundToMultiple(valueToRound: number, roundToNearest: number = 25): number {
        // Custom rounding function. Logic ported from legacy vb.Net app
        let result: number = 0;

        if (valueToRound === 0 || roundToNearest === 0) { return result; }

        const halfRound: number = parseInt((Math.abs(roundToNearest) / 2).toFixed(0), 10);

        const roundToNearestInt: number = parseInt(Math.abs(roundToNearest).toFixed(0), 10);
        const valueToRoundInt: number = parseInt(Math.abs(valueToRound).toFixed(0), 10);

        const valueModNumber = valueToRoundInt % roundToNearestInt;

        if (valueModNumber > halfRound) {
            result = valueToRoundInt + (roundToNearestInt - valueModNumber);
        } else {
            result = valueToRoundInt - valueModNumber;
        }

        // console.log(`getCustomRoundToMultiple valueToRound ${valueToRound} numberToRoundInt ${roundToNearestInt} halfRound ${halfRound} valueModNumber ${valueModNumber} result ${result}`);

        return parseInt(result.toFixed(0), 10);
    }

    public static getCustomInventoryRoundToMultiple(valueToRound: number, roundToNearest: number = 25): number {
        // let val = Math.round(valueToRound);
        // let remain = val % roundToNearest;
        // let res = val - remain;
        // console.log(`getCustomInventoryRoundToMultiple valueToRound ${valueToRound} val ${val} remain ${remain} res ${res}`);
        // return res;
        return Math.round(valueToRound / roundToNearest) * roundToNearest;
    }    
}



// WEBPACK FOOTER //
// ./src/common/formatters.tsx